import { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import { useForm, SubmitHandler } from "react-hook-form";

import CollapsibleTable from "../../components/collapsible-table";
import Avatar from "@mui/material/Avatar";
import AddIcon from "@mui/icons-material/Add";
import Button from "@mui/material/Button";
import useCompanies from "../../hooks/companies";
import TagColor from "../../components/tag-color";
import UploadButton from "../../components/upload-button";
import Drawer from "@mui/material/Drawer";
import TextField from "@mui/material/TextField";

const CompaniesList = () => {
  const { get: getCompanies, uploadPhoto, create, edit } = useCompanies();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<Company>();

  const loadData = useCallback(() => {
    const fn = async () => {
      const resp = await getCompanies();
      setCompanies(resp);
    };
    fn();
  }, []);

  const onSubmit: SubmitHandler<Company> = (data) => {
    if (isEdit) {
      edit(data.id, data);
    } else {
      create(data);
    }
    loadData();
    toggleDrawer({});
  };
  const uploadLogo = useCallback(async (userId: string, file: File) => {
    setIsLoading(true);
    await uploadPhoto(userId, file);
    await loadData();
    setIsLoading(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const toggleDrawer = ({ isEdit = false }: { isEdit?: boolean }) => {
    setIsEdit(isEdit);
    reset();
    setOpenDrawer(!openDrawer);
  };
  const openEdit = useCallback((company: Company) => {
    toggleDrawer({ isEdit: true });

    for (const key in company) {
      setValue(key as keyof Company, company[key as keyof Company]);
    }
  }, []);
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        gap: 2,
        padding: "48px",
        paddingTop: "80px",
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          gap: 2,
          paddingTop: "80px",
          width: "100%",
        }}
      >
        <Button
          size="small"
          variant="contained"
          color="secondary"
          onClick={() => toggleDrawer({})}
        >
          <AddIcon />
          Add comppany
        </Button>
      </Box>

      <CollapsibleTable
        header={[
          "Name",
          "Fantasy name",
          "Logo",
          "Primary color",
          "Primary color contrast",
          "Secondary color",
          "Secondary color contrast",
          "",
          "",
        ]}
        rows={companies.map((company) => {
          return {
            cells: [
              company.name,
              company.fantasyName,
              <Avatar
                alt="Logo"
                src={company.logo}
                sx={{ width: 56, height: 56 }}
              />,
              <TagColor color={company.colors.primary} />,
              <TagColor color={company.colors.foregroundPrimary} />,
              <TagColor color={company.colors.secondary} />,
              <TagColor color={company.colors.foregroundSecondary} />,
              <UploadButton
                id={company.id}
                label={company.logo === null ? "Upload logo" : "Change logo"}
                uploadPhoto={uploadLogo}
                isLoading={isLoading}
              />,
              <Button
                size="small"
                variant="outlined"
                color="primary"
                onClick={() => openEdit(company)}
              >
                Edit
              </Button>,
            ],
          };
        })}
      />
      <Drawer anchor={"right"} open={openDrawer} onClose={toggleDrawer}>
        <div className="p-[24px] pt-[88px]">
          {isEdit ? "Edit" : "Add"} Company
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col gap-[24px]"
          >
            <TextField
              id="outlined-basic"
              label="Company fantasy name"
              variant="outlined"
              {...register("fantasyName", { required: true })}
            />
            <TextField
              id="outlined-basic"
              label="Company name"
              variant="outlined"
              {...register("name", { required: true })}
            />
            <TextField
              id="outlined-basic"
              label="Company address"
              variant="outlined"
              {...register("address", { required: true })}
            />
            <TextField
              id="outlined-basic"
              label="Company Phone"
              variant="outlined"
              {...register("phone", { required: true })}
            />
            <TextField
              id="outlined-basic"
              label="Company e-mail"
              variant="outlined"
              {...register("email", { required: true })}
            />
            <TextField
              id="outlined-basic"
              label="Company VAT"
              variant="outlined"
              {...register("vat", { required: true })}
            />

            <div className="flex flex-row gap-[24px]">
              <div className="flex flex-row">
                <TextField
                  id="filled-basic"
                  label="Primary color"
                  variant="outlined"
                  {...register("colors.primary", { required: true })}
                />
                <TagColor color={watch("colors.primary")} />
              </div>
              <div className="flex flex-row">
                <TextField
                  id="filled-basic"
                  label="Primary color contrast"
                  variant="outlined"
                  {...register("colors.secondary", { required: true })}
                />
                <TagColor color={watch("colors.secondary")} />
              </div>
            </div>
            <div className="flex flex-row  gap-[24px]">
              <div className="flex flex-row">
                <TextField
                  id="filled-basic"
                  label="Primary color"
                  variant="outlined"
                  {...register("colors.foregroundPrimary", { required: true })}
                />
                <TagColor color={watch("colors.foregroundPrimary")} />
              </div>
              <div className="flex flex-row">
                <TextField
                  id="filled-basic"
                  label="Primary color contrast"
                  variant="outlined"
                  {...register("colors.foregroundSecondary", {
                    required: true,
                  })}
                />
                <TagColor color={watch("colors.foregroundSecondary")} />
              </div>
            </div>
            <Button type="submit" variant="contained" color="secondary">
              Save
            </Button>
          </form>
        </div>
      </Drawer>
    </Box>
  );
};

export default CompaniesList;
